import React from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";

import { tRep } from "tmt_api/src/types/types.validator";

import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdStack, PdText } from "src/components/layout";
import { PdModal, PdModalTitle } from "src/components/overlay";
import { documentRep } from "src/db/rep.db";
import { kColors } from "src/kColors";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { tModalComponentProps } from "src/redux/modals/types";
import { useIsMobile } from "../ui";
import { kStyleConst } from "src/kStyle";
import { PdInput, PdInputPercentage } from "src/components/forms/input";
import { putOrderConfirmRep } from "src/util/apiNew";
import { PdFormLabel } from "src/components/form-control";
import { formatMoney } from "src/util/gFormatting";
import { PdSwitch } from "src/components/forms/switch";

type tModalParams = {
  orderId: string;
  orderTotal: number;
  repIdToAssign: string;
  isRepAssigned: boolean;
};

const DEFAULT_NOTE_MESSAGE = `Hi there,

Thanks for writing this order but this account is in house and not a sales rep account. We iill contact you shortly with additional details!`;

const ModalContent: React.FC<
  Pick<tModalComponentProps<tModalParams>, "onResolve" | "onClose" | "orderId" | "orderTotal"> & { repToAssign: tRep }
> = ({ onClose, orderId, orderTotal, repToAssign }) => {
  const rIsMobile = useIsMobile();
  const [view, setView] = React.useState<"start-message" | "enterFee" | "messageRep" | "success">("start-message");
  const [sendMessage, setSendMessage] = React.useState(false);
  const [messageToRep, setMessageToRep] = React.useState(DEFAULT_NOTE_MESSAGE);
  const [repFeeToAssign, setRepFeeToAssign] = React.useState<number | null>(null);

  const handleOnPressNo = async () => {
    setView("messageRep");
  };

  const handleOnPressYes = () => {
    setView("enterFee");
  };

  const handleOnPressConfirm = async () => {
    if (!repFeeToAssign) return;
    await putOrderConfirmRep(orderId, { repId: repToAssign.id, repFeeToAssign: repFeeToAssign / 100 });
    setView("success");
  };

  const handleOnPressSendProceedToOrder = async () => {
    if (!messageToRep) return;
    await putOrderConfirmRep(orderId, { repId: repToAssign.id, messageToRep });
    onClose();
  };

  const handleOnPressGoToOrder = async () => {
    onClose();
  };

  if (view === "start-message") {
    return (
      <>
        <PdModalTitle style={styles.title}>A Sales Rep wrote this order!</PdModalTitle>
        <PdText>
          {`${repToAssign.name} wrote & shared this suggested order with the Retailer. Do you want to give ${repToAssign.name} a commission for this order?`}
        </PdText>
        <PdStack
          direction={rIsMobile ? "vertical" : "horizontal"}
          layout={[1, 1]}
          isReversed={rIsMobile}
          style={styles.mt6}
        >
          <PdButton onPress={handleOnPressNo} variant="outline">
            <PdButtonLabel>No</PdButtonLabel>
          </PdButton>
          <PdButton onPress={handleOnPressYes} style={rIsMobile ? styles.mb4 : {}}>
            <PdButtonLabel>Yes</PdButtonLabel>
          </PdButton>
        </PdStack>
      </>
    );
  }

  if (view === "enterFee") {
    return (
      <>
        <PdModalTitle style={styles.title}>Enter Commission Amount</PdModalTitle>
        <PdText
          style={styles.mb1}
        >{`Please enter the commission percentage (%) you want ${repToAssign.name} to receive for this order.`}</PdText>
        <PdStack direction={rIsMobile ? "vertical" : "horizontal"} layout={[1, 1]}>
          <View style={styles.pdInputGroup}>
            <PdInputPercentage
              style={[styles.widthFull, styles.border0]}
              value={repFeeToAssign}
              onChangeNumber={setRepFeeToAssign}
            />
            <PdText style={styles.mr2}>%</PdText>
          </View>
          <View style={styles.alignEnd}>
            <PdFormLabel>{`${repToAssign.name} will receive`}</PdFormLabel>
            <PdText>{repFeeToAssign ? formatMoney(orderTotal * (repFeeToAssign / 100)) : "-"}</PdText>
          </View>
        </PdStack>
        <PdButton onPress={handleOnPressConfirm} isDisabled={!repFeeToAssign} style={styles.mt6}>
          <PdButtonLabel>Approve</PdButtonLabel>
        </PdButton>
      </>
    );
  }

  if (view === "messageRep") {
    return (
      <>
        <PdModalTitle style={styles.title}>Message the Rep</PdModalTitle>
        <PdText
          style={styles.mb1}
        >{`Would you like to message ${repToAssign.name} and inform them why they won't receive a commission on this order`}</PdText>
        <View style={[styles.flexRow, styles.alignCenter, styles.mlAuto]}>
          <PdText style={styles.mr2}>{sendMessage ? "Yes" : "No"}</PdText>
          <PdSwitch value={sendMessage} onValueChange={setSendMessage} />
        </View>
        {sendMessage && (
          <PdInput
            multiline
            numberOfLines={5}
            value={messageToRep}
            onChangeText={setMessageToRep}
            placeholder="Note"
            style={styles.mt3}
          />
        )}
        <PdButton onPress={handleOnPressSendProceedToOrder} style={styles.mt6}>
          <PdButtonLabel>{sendMessage ? "Send & Proceed to Order" : "Proceed to Order"}</PdButtonLabel>
        </PdButton>
      </>
    );
  }
  if (view === "success") {
    return (
      <>
        <PdModalTitle style={styles.title}>Thank you!</PdModalTitle>
        <PdText style={styles.mb1}>Please proceed to process the order as you normally would.</PdText>
        <PdText
          style={styles.mb1}
        >{`If you'd like to assign ${repToAssign.name} to this retailer, please go to your Customers page, find and click the Retailer name, then enter ${repToAssign.name}'s email and commission amount. Future orders this retailer places will be attributed to ${repToAssign.name}`}</PdText>
        <PdButton onPress={handleOnPressGoToOrder} style={styles.mt6}>
          <PdButtonLabel>Go to Order</PdButtonLabel>
        </PdButton>
      </>
    );
  }

  return null;
};

const AssignRepOrderModal: React.FC<tModalComponentProps<tModalParams>> = ({
  onResolve,
  onClose,
  orderId,
  orderTotal,
  repIdToAssign,
}) => {
  const [repToAssign, setRepToAssign] = React.useState<tRep | undefined>(undefined);

  React.useEffect(() => {
    const unsubscribe = documentRep(repIdToAssign).onSnapshot((document) => {
      setRepToAssign(document.data());
    });

    return () => {
      unsubscribe();
    };
  }, [repIdToAssign]);

  return (
    <PdModal closeOnOverlayClick={false}>
      <ScrollView contentContainerStyle={styles.modal} showsHorizontalScrollIndicator={false}>
        {!repToAssign ? (
          <View style={[styles.center, styles.widthFull]}>
            <ActivityIndicator color={kColors.brandDarkGreen} size="large" />
          </View>
        ) : (
          <ModalContent
            orderId={orderId}
            orderTotal={orderTotal}
            repToAssign={repToAssign}
            onClose={onClose}
            onResolve={onResolve}
          />
        )}
      </ScrollView>
    </PdModal>
  );
};

const useAssignRepOrderModal = (): tUseModal<tModalParams> => {
  return useModal<tModalParams>(AssignRepOrderModal);
};

export default useAssignRepOrderModal;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  border0: {
    borderWidth: 0,
  },
  center: {
    alignContent: "center",
    justifyContent: "center",
  },
  flexRow: {
    flexDirection: "row",
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  modal: {
    maxWidth: 400,
    padding: 8 * kStyleConst.kSpacing,
    width: "100%",
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  title: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  widthFull: {
    width: "100%",
  },
});
